// FILE: src/services/journeyService.js
//
// Journey Activation onboarding — service layer (ships DARK behind the
// journeyOnboardingEnabled flag; see src/config/featureFlags.js).
//
// Design contract (activation gate 3 / Journey UX review, 2026-08-12/13):
//   • Observed intent ALWAYS outranks declared intent. Any mode selection or
//     completed session permanently suppresses the journey intervention.
//   • Declared journey stage is assistance, never a gate: it is stored in
//     learner_profiles.preferences.journey (JSONB — no migration) and remains
//     mutable forever.
//   • First-time and returning-dormant learners share the same stages and
//     routing; only the presentation differs (panel vs. strip).
//   • The dormant interval is a HYPOTHESIS, not a product rule: 14 days by
//     default, overridable per environment (REACT_APP_JOURNEY_DORMANT_DAYS).
//
// All persistence is fire-and-forget: a failed profile write must never
// block or break the learner experience.

import { getActiveCertIds, getBaselineColumn } from '../config/exams';
import { updateLearnerProfile } from './learnerProfileService';
import { trackEvent } from './telemetryService';
import { JOURNEY_DORMANT_DAYS } from '../config/featureFlags';

// ── Stages ───────────────────────────────────────────────────────────────────

export const JOURNEY_STAGES = [
  { id: 'exploring', label: 'Exploring',            blurb: "Seeing what's involved" },
  { id: 'learning',  label: 'Learning',             blurb: 'Building my knowledge' },
  { id: 'preparing', label: 'Preparing',            blurb: 'Studying for my exam' },
  { id: 'testing',   label: 'Testing my readiness', blurb: 'Am I ready yet?' },
];

// ── Exploring content (Content Factory) ──────────────────────────────────────
//
// Published exploratory destinations per cert. This map mirrors
// content/registry.json (system of record — CRA cannot import it from src/,
// so journeyService.test.js validates every entry against the registry and
// fails the build if they drift). Certs with NO appropriate published
// article are deliberately absent: the UI falls back to the 5-question
// sampler instead of inventing a destination, and the gap is tracked as a
// Content Factory backlog opportunity (stage6-portfolio-backlog.md:
// ACP-003 "PMI-ACP vs CSM (vs PMP)", CPMAI-004 "CPMAI vs PMP").

export const EXPLORING_CONTENT = {
  pmp: {
    primary:   { contentId: 'PMP-003',   url: '/articles/how-hard-is-the-pmp-exam/',        label: 'How hard is the PMP exam, really?' },
    secondary: { contentId: 'PMP-001',   url: '/articles/pmp-passing-score/',               label: 'What score do you need to pass?' },
  },
  cpmai: {
    primary:   { contentId: 'CPMAI-002', url: '/articles/pmi-cpmai-certification-guide/',   label: 'The complete CPMAI guide' },
    secondary: { contentId: 'CPMAI-003', url: '/articles/cpmai-passing-score/',             label: "What's the CPMAI passing score?" },
  },
};

/** Published exploratory content for a cert, or null (fallback = sampler). */
export function getExploringContent(certId) {
  return EXPLORING_CONTENT[(certId || '').toLowerCase()] || null;
}

// ── Journey state (learner_profiles.preferences.journey) ─────────────────────

/** @returns {object} the journey sub-object of profile preferences ({} when absent). */
export function getJourneyState(profile) {
  return (profile && profile.preferences && profile.preferences.journey) || {};
}

/**
 * Merges a patch into preferences.journey and upserts the profile.
 * Fire-and-forget safe: resolves to the merged journey object either way.
 */
export async function persistJourneyPatch(userId, profile, patch) {
  const journey = { ...getJourneyState(profile), ...patch };
  if (userId) {
    try {
      const preferences = { ...((profile && profile.preferences) || {}), journey };
      await updateLearnerProfile(userId, { preferences });
    } catch (_) { /* never block the UI on a preferences write */ }
  }
  return journey;
}

// ── Observed intent ──────────────────────────────────────────────────────────
//
// mode_selected fires in App.handleSelectMode — the same signal the
// activation baseline uses for "clear intent". markLocalIntent() is called
// there so suppression takes effect immediately and across reloads on this
// device; the journey surface reconciles it into the profile (cross-device)
// the next time it mounts.

const LOCAL_INTENT_KEY = 'pmPixel.journeyIntentObserved';

export function markLocalIntent() {
  try { window.localStorage.setItem(LOCAL_INTENT_KEY, new Date().toISOString()); } catch (_) { /* private mode */ }
}

export function hasLocalIntent() {
  try { return !!window.localStorage.getItem(LOCAL_INTENT_KEY); } catch (_) { return false; }
}

/** True when the learner has demonstrated intent: mode selection, any
 *  completed session, or any baseline. Permanent suppression signal. */
export function hasObservedIntent({ profile, sessions }) {
  if (hasLocalIntent()) return true;
  if (getJourneyState(profile).intentObservedAt) return true;
  if (Array.isArray(sessions) && sessions.length > 0) return true;
  if (profile) {
    for (const cert of getActiveCertIds()) {
      if (profile[getBaselineColumn(cert)]) return true;
    }
  }
  return false;
}

// ── Treatment (which presentation, if any) ───────────────────────────────────

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Decides the journey treatment for an eligible render.
 *
 * @returns {'first_time'|'dormant'|'collapsed'|null}
 *   first_time — full panel (never dismissed, not returning after a gap)
 *   dormant    — light strip (returned after >= dormantDays of inactivity)
 *   collapsed  — "Set your journey" link only (dismissed, or stage already set)
 *   null       — nothing (not confirmed, observed intent, or strip exhausted)
 */
export function computeJourneyTreatment({ user, profile, sessions, now = new Date(), dormantDays = JOURNEY_DORMANT_DAYS }) {
  if (!user || !user.email_confirmed_at) return null;
  if (hasObservedIntent({ profile, sessions })) return null;

  const j = getJourneyState(profile);
  const windowMs = Math.max(1, dormantDays) * DAY_MS;
  const nowMs = now.getTime();
  const lastSeen = j.lastSeenAt ? Date.parse(j.lastSeenAt) : null;
  const created = Date.parse(user.created_at || user.email_confirmed_at);
  const isDormantReturn = lastSeen != null
    ? nowMs - lastSeen >= windowMs
    : nowMs - created >= windowMs;

  if (isDormantReturn) {
    const dismissals = Array.isArray(j.stripDismissedAt) ? j.stripDismissedAt : [];
    if (dismissals.length >= 2) return null;                       // re-shown once already — never again
    if (dismissals.length === 1
        && nowMs - Date.parse(dismissals[0]) < windowMs) return null; // still inside the suppression window
    return 'dormant';
  }

  if (j.stage || j.promptDismissedAt) return 'collapsed';
  return 'first_time';
}

// ── Telemetry ────────────────────────────────────────────────────────────────
//
// Journey events are 'growth' category and carry dimensions only:
//   treatment      'first_time' | 'dormant'
//   journey_stage  'exploring' | 'learning' | 'preparing' | 'testing' | 'unsure'
//   cert           examId when known
//   from / to      stage change transitions
//   target         which recommendation was started ('article' | 'micro' |
//                  'baseline' | 'practice' | 'full' | 'plan')
//   content_id     Content Factory article id when target === 'article'
// journey_prompt_viewed is deduped once per app session so the funnel counts
// prompts, not re-renders. mode_selected (existing) remains the canonical
// observed-intent event — journey events add context, never replace it.

const PROMPT_VIEWED_KEY = 'pmPixel.journeyPromptViewed';

export function trackJourneyEvent(name, { treatment, stage, cert, from, to, target, contentId } = {}) {
  trackEvent(name, 'growth', {
    treatment,
    journey_stage: stage,
    cert,
    from,
    to,
    target,
    content_id: contentId,
  });
}

/** Emits journey_prompt_viewed at most once per app session. */
export function trackPromptViewedOnce(treatment) {
  try {
    if (window.sessionStorage.getItem(PROMPT_VIEWED_KEY)) return;
    window.sessionStorage.setItem(PROMPT_VIEWED_KEY, '1');
  } catch (_) { /* storage unavailable — still emit */ }
  trackJourneyEvent('journey_prompt_viewed', { treatment });
}
